import { createTheme, Theme } from "@mui/material";
import { darkScrollBarStyles, scrollBarStyles } from "./scroll-bar";
import { inputsCustomizations } from "./inputs-styles";

// Extend the Palette interface to include custom colors
declare module "@mui/material/styles" {
  interface Palette {
    freeTable: Palette["primary"];
    occupiedTable: Palette["primary"];
    color3: Palette["primary"];
    color4: Palette["primary"];
    color5: Palette["primary"];
    color6: Palette["primary"];
    color7: Palette["primary"];
    color8: Palette["primary"];
    color9: Palette["primary"];
    color10: Palette["primary"];
    color11: Palette["primary"];
    color12: Palette["primary"];
    color13: Palette["primary"];
    color14: Palette["primary"];
    color15: Palette["primary"];
    CommonWhite: Palette["primary"];
    CommonBlack: Palette["primary"];
    available: Palette["primary"];
    occupied: Palette["primary"];
    offline: Palette["primary"];
  }

  interface PaletteOptions {
    freeTable?: PaletteOptions["primary"];
    occupiedTable?: PaletteOptions["primary"];
    color3?: PaletteOptions["primary"];
    color4?: PaletteOptions["primary"];
    color5?: PaletteOptions["primary"];
    color6?: PaletteOptions["primary"];
    color7?: PaletteOptions["primary"];
    color8?: PaletteOptions["primary"];
    color9?: PaletteOptions["primary"];
    color10?: PaletteOptions["primary"];
    color11?: PaletteOptions["primary"];
    color12?: PaletteOptions["primary"];
    color13?: PaletteOptions["primary"];
    color14?: PaletteOptions["primary"];
    color15?: PaletteOptions["primary"];
    CommonWhite: PaletteOptions["primary"];
    CommonBlack: PaletteOptions["primary"];
    available: PaletteOptions["primary"];
    occupied: PaletteOptions["primary"];
    offline: PaletteOptions["primary"];
  }
}

// Define the theme configuration
export const darkTheme: Theme = createTheme({
  typography: {
    fontFamily: ["Lato"].join(","),
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          ...darkScrollBarStyles,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          '&.MuiTableCell-root.MuiTableCell-head': {
            backgroundColor: "#1E1E1E", // Light gray for table header
            color: "#f0f0f0", // Dark gray for text
          },
        },
      },
    },
  },
  palette: {
    mode: 'dark', // Enable dark mode
    freeTable: { main: "#88B04B" }, // Coral Red
    occupiedTable: { main: "#F44336" }, // Teal Blue
    color3: { main: "#88B04B" }, // Green
    color4: { main: "#F99D9B" }, // Light Pink
    color5: { main: "#92A8D1" }, // Light Blue
    color6: { main: "#955251" }, // Burgundy
    color7: { main: "#B9D3C1" }, // Mint
    color8: { main: "#F6F6F6" }, // Very Light Gray
    color9: { main: "#F2C94C" }, // Yellow
    color10: { main: "#EAB543" }, // Gold
    color11: { main: "#D9BF77" }, // Tan
    color12: { main: "#323232ff" }, // White
    color13: { main: "#FFFFFF" }, // Off White
    color14: { main: "#EAEAEA" }, // Light Gray
    color15: { main: "#C1C1C1" }, // Gray
    CommonWhite: { main: "#fff" },
    CommonBlack: { main: "#000" },
    available: { main: "#88B04B" },
    occupied: { main: "#F2C94C" },
    offline: { main: "#C1C1C1" },
    primary: {
      main: '#009688', // Deep Purple
      contrastText: '#FFFFFF', // White for contrast
      light: "rgb(51 171 159 / 21%)"
    },
    secondary: {
      main: '#009688', // Teal
      contrastText: '#B0BEC5', // Light Gray for contrast
    },
    error: {
      main: '#F44336', // Red for error states
      contrastText: '#FFFFFF', // White for contrast
    },
    success: {
      main: '#4CAF50', // Green for success
      contrastText: '#FFFFFF', // White for contrast
    },
    warning: {
      main: '#FFC107', // Amber for warnings
      contrastText: '#000000', // Black for contrast
    },
    background: {
      default: '#121212', // Black for the default background
      paper: '#1E1E1E', // Dark Gray for paper elements
    },
    text: {
      primary: '#FFFFFF', // White for primary text
      secondary: '#B0BEC5', // Light Gray for secondary text
    },
    action: {
      hover: '#424242', // Dark Gray for hover states
      selected: '#009688', // Teal for selected states
    },
  },
});